import { FinalTournamentView } from '../components/scoring/FinalTournamentView';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../components/AuthProvider';
import { ClearScoresDialog } from '../components/scoring/ClearScoresDialog';
import { RemoveShooterDialog } from '../components/scoring/RemoveShooterDialog';
import { GenderRosterFilter } from '../components/scoring/GenderRosterFilter';
import { removedToast, scoresClearedToast, pushScoringToast } from '../components/common/admin-toasts';
import { ApiError, apiFetch } from '../lib/api';
import { useScoringTournament, type ScoreRow, type TournamentDetail } from '../hooks/useScoringTournament';
import {
  findShooterByKey,
  isFromListState,
  nextShooter,
  scoringListPath,
  scoringShooterPath,
} from '../lib/scoring-routes';
import {
  RIFLE_TOURNAMENT_SLUG,
  SHOOTER_GENDERS,
  SHOOTER_GENDER_LABELS,
  SHOT_RANGE_MESSAGE,
  WHOLE_SHOT_MESSAGE,
  formatDecimalTenths,
  formatMaxDisplay,
  formatPointsTenths,
  summaryFormat,
  type ShooterGender,
} from '../lib/shooting';
import { parseGenderFilter, type GenderFilter } from '../lib/scoreboard-view';
import type { ShooterRow } from '../hooks/useScoringTournament';
import { draftTenths, wholeDraftTenths, isCompleteShot, normalizeShotDraft, runningTotal, shotDisplay } from '../lib/shot-entry';
import { strictWholeShotEntry, showShotPoints, wholeNumberScoring } from '../lib/round1';
import { scoringTitle } from '../lib/page-title';
import { mergeScoringLiveBoard, useScoringLiveBoard } from '../hooks/useScoringLiveBoard';
import { completedShooters, nextTeamShooter } from '../lib/scoring-navigation';

type CellStatus = 'empty' | 'typing' | 'saving' | 'saved' | 'error';

interface Cell {
  draft: string;
  inputRejected?: boolean;
  savedTenths: number | null;
  scoreId: string | null;
  status: CellStatus;
  error: string | null;
}

const cellKey = (shot: number) => String(shot);
const shotNumbers = (count: number) => Array.from({ length: count }, (_, index) => index + 1);

function tenthsFromDisplay(display: string): number {
  const [whole, frac = '0'] = display.split('.');
  return Number(whole) * 10 + Number(frac.slice(0, 1) || '0');
}

function shownShot(tenths: number, whole: boolean): string {
  return whole ? showShotPoints(tenths, true) : shotDisplay(tenths);
}

function cellsFromScores(scores: ScoreRow[], shooterId: string, shots: number, whole = false) {
  const cells: Record<string, Cell> = {};
  for (const shot of shotNumbers(shots)) {
    cells[cellKey(shot)] = { draft: '', savedTenths: null, scoreId: null, status: 'empty', error: null };
  }
  for (const score of scores) {
    // Old whole-series totals (shot 0) are not shown or editable here.
    if (score.shooterId !== shooterId || score.shotNumber < 1) continue;
    const key = cellKey(score.shotNumber);
    if (!cells[key]) continue;
    const tenths = tenthsFromDisplay(score.pointsDisplay);
    cells[key] = { draft: shownShot(tenths, whole), savedTenths: tenths, scoreId: score.id, status: 'saved', error: null };
  }
  return cells;
}

/** /admin/scoring/:slug/shooter/:shooterKey — type each shot; every shot saves on its own. */
export function ScoringShooterPage() {
  const { isAdmin: admin, isOrganizer } = useAuth();
  const { slug: slugParam, shooterKey = '' } = useParams();
  const isAdmin = admin || Boolean(isOrganizer && slugParam === RIFLE_TOURNAMENT_SLUG);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const { slug, detail, notFound, error, setDetail } = useScoringTournament(slugParam);
  useScoringLiveBoard(slug, isAdmin && Boolean(detail), (board) => setDetail((current) => mergeScoringLiveBoard(current, board)));
  const groupQuery = null;
  const genderParam = parseGenderFilter(searchParams.get('gender'));
  const genderFilter: GenderFilter = isAdmin ? genderParam : 'all';
  const genderQuery = genderParam === 'all' ? null : genderParam;

  const shooter = useMemo(() => (detail ? findShooterByKey(detail.shooters, shooterKey) : null), [detail, shooterKey]);
  const shooterId = shooter?.id ?? null;
  useEffect(() => {
    document.title = scoringTitle(shooter?.name, detail?.tournament.title);
  }, [shooter?.name, detail?.tournament.title]);
  const listPath = scoringListPath(slug, query, groupQuery, genderQuery);
  const cameFromList = isFromListState(location.state);
  const added = cameFromList && (location.state as { added?: unknown }).added === true;
  const format = summaryFormat(detail?.tournament ?? {});
  const { shotsPerShooter, countedShots } = format;
  const allCount = countedShots >= shotsPerShooter;
  const SHOT_NUMBERS = useMemo(() => shotNumbers(shotsPerShooter), [shotsPerShooter]);
  const formatKey = `${shooterId ?? ''}:${shotsPerShooter}`;
  // Fill straight away when the tournament is already loaded (Back/Next, cached data), else after it loads.
  const [cells, setCells] = useState<Record<string, Cell>>(() =>
    detail && shooterId ? cellsFromScores(detail.scores, shooterId, shotsPerShooter, wholeNumberScoring(detail.tournament)) : {},
  );
  const [preparedFor, setPreparedFor] = useState<string | null>(() => (detail && shooterId ? formatKey : null));
  const whole = wholeNumberScoring(detail?.tournament);
  const inputs = useRef<Map<string, HTMLInputElement>>(new Map());
  const pending = useRef(new Map<string, { draft: string; promise: Promise<boolean> }>());
  const persisted = useRef(new Map<string, { savedTenths: number | null; scoreId: string | null }>());
  const [saveCount, setSaveCount] = useState(0);
  const activeForm = useRef(formatKey);
  activeForm.current = formatKey;
  const mounted = useRef(true);
  const autoAdvance = useRef<string | null>(null);
  const focusFirst = useRef(false);
  const cellsRef = useRef(cells);
  cellsRef.current = cells;
  const [clearOpen, setClearOpen] = useState(false);
  const [clearedMessage, setClearedMessage] = useState<string | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [genderSaving, setGenderSaving] = useState(false);
  const [genderError, setGenderError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [shooterKey]);

  // Fresh grid for each shooter (including "Next shooter"), filled from what is already saved.
  useEffect(() => {
    if (!detail || !shooterId || preparedFor === formatKey) return;
    setPreparedFor(formatKey);
    const fresh = cellsFromScores(detail.scores, shooterId, shotsPerShooter, wholeNumberScoring(detail.tournament));
    cellsRef.current = fresh;
    setCells(fresh);
    for (const [key, cell] of Object.entries(fresh)) {
      const id = `${formatKey}:${key}`;
      if (!pending.current.has(id)) persisted.current.set(id, { savedTenths: cell.savedTenths, scoreId: cell.scoreId });
    }
    setSaveCount([...pending.current.keys()].filter((id) => id.startsWith(`${formatKey}:`)).length);
    autoAdvance.current = null;
  }, [detail, shooterId, preparedFor, formatKey, shotsPerShooter]);

  const order = useMemo(() => SHOT_NUMBERS.map(cellKey), [SHOT_NUMBERS]);

  useEffect(() => {
    if (preparedFor !== formatKey || !focusFirst.current) return;
    focusFirst.current = false;
    inputs.current.get(order[0])?.focus();
  }, [preparedFor, formatKey, order]);

  const patchCell = useCallback((key: string, patch: Partial<Cell>) => {
    const current = cellsRef.current;
    if (!current[key]) return;
    const next = { ...current, [key]: { ...current[key], ...patch } };
    cellsRef.current = next;
    setCells(next);
  }, []);

  useEffect(() => {
    mounted.current = true;
    const warn = (event: BeforeUnloadEvent) => {
      if (pending.current.size > 0 || Object.values(cellsRef.current).some((cell) => ['typing', 'saving', 'error'].includes(cell.status))) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      mounted.current = false;
      window.removeEventListener('beforeunload', warn);
    };
  }, []);

  function focusAfter(key: string) {
    if (document.activeElement !== inputs.current.get(key)) return;
    const index = order.indexOf(key);
    const nextKey = order[index + 1];
    const nextInput = nextKey ? inputs.current.get(nextKey) : null;
    if (nextInput) nextInput.focus();
    else inputs.current.get(key)?.blur();
  }

  function commit(shot: number, draftOverride?: string): Promise<boolean> {
    const key = cellKey(shot);
    const cell = cellsRef.current[key];
    if (!cell || cell.inputRejected || !detail || !shooterId) return Promise.resolve(false);
    const draft = (draftOverride ?? cell.draft).trim();
    const identity = `${formatKey}:${key}`;
    const previous = pending.current.get(identity);
    if (previous?.draft === draft) return previous.promise;
    if (!persisted.current.has(identity)) persisted.current.set(identity, { savedTenths: cell.savedTenths, scoreId: cell.scoreId });
    // Only corrections to the SAME shot wait. Different shot boxes stay responsive.
    const promise = (previous?.promise ?? Promise.resolve()).then(() => saveShot(shot, draft, identity)).finally(() => {
      if (pending.current.get(identity)?.promise === promise) pending.current.delete(identity);
      if (mounted.current) setSaveCount([...pending.current.keys()].filter((id) => id.startsWith(`${activeForm.current}:`)).length);
    });
    pending.current.set(identity, { draft, promise });
    setSaveCount([...pending.current.keys()].filter((id) => id.startsWith(`${formatKey}:`)).length);
    return promise;
  }

  async function saveShot(shot: number, draft: string, identity: string): Promise<boolean> {
    if (!detail || !shooterId) return false;
    const key = cellKey(shot);
    const stored = persisted.current.get(identity)!;
    const current = () => mounted.current && activeForm.current === formatKey;
    const untouched = () => current() && !cellsRef.current[key]?.inputRejected && (cellsRef.current[key]?.draft ?? '').trim() === draft;
    const patch = (value: Partial<Cell>) => { if (current()) patchCell(key, value); };

    if (!draft) {
      if (!stored.scoreId) {
        if (untouched()) patch({ status: 'empty', error: null });
        return false;
      }
      if (untouched()) patch({ status: 'saving', error: null });
      const scoreId = stored.scoreId;
      try {
        const body = await apiFetch<{ leaderboard?: TournamentDetail['leaderboard'] | null }>(
          `/api/admin/shooting/scores/${scoreId}?ack=persisted`,
          { method: 'DELETE' },
        );
        stored.savedTenths = null;
        stored.scoreId = null;
        if (current()) setDetail((value) => ({ ...value, scores: value.scores.filter((score) => score.id !== scoreId), leaderboard: body.leaderboard ?? value.leaderboard }));
        patch({ ...stored, ...(untouched() ? { status: 'empty' as const, error: null } : {}) });
      } catch (err) {
        if (untouched()) patch({ status: 'error', error: err instanceof ApiError ? err.message : 'Could not clear that shot.' });
      }
      return false;
    }

    const tenths = whole ? wholeDraftTenths(draft) : draftTenths(draft);
    if (tenths == null) {
      if (untouched()) patch({ status: 'error', error: whole ? WHOLE_SHOT_MESSAGE : SHOT_RANGE_MESSAGE });
      return false;
    }
    if (tenths === stored.savedTenths) {
      if (untouched()) patch({ ...stored, draft: shownShot(tenths, whole), status: 'saved', error: null });
      return true;
    }
    if (untouched()) patch({ status: 'saving', error: null });
    try {
      const body = await apiFetch<{ leaderboard?: TournamentDetail['leaderboard']; score: ScoreRow }>(
        `/api/admin/shooting/tournaments/${detail.tournament.id}/scores?ack=persisted`,
        {
          method: 'POST',
          body: JSON.stringify({
            shooterId,
            shotNumber: shot,
            points: wholeNumberScoring(detail.tournament) ? formatPointsTenths(tenths) : shotDisplay(tenths),
          }),
        },
      );
      stored.savedTenths = tenths;
      stored.scoreId = body.score.id;
      if (current()) setDetail((value) => ({
        ...value,
        leaderboard: !body.leaderboard || (body.leaderboard.updatedAt && value.leaderboard.updatedAt && body.leaderboard.updatedAt < value.leaderboard.updatedAt) ? value.leaderboard : body.leaderboard,
        scores: [...value.scores.filter((score) => score.shooterId !== body.score.shooterId || score.shotNumber !== body.score.shotNumber), body.score],
      }));
      patch({ ...stored, ...(untouched() ? { draft: shownShot(tenths, whole), status: 'saved' as const, error: null } : {}) });
    } catch (err) {
      if (untouched()) {
        patch({
          status: 'error',
          error: err instanceof ApiError ? err.message : 'Not saved. Check the connection and tap the shot again.',
        });
      }
      return false;
    }
    return true;
  }

  function finishWholeShot(shot: number, draft: string) {
    if (shot >= shotsPerShooter) autoAdvance.current = formatKey;
    void commit(shot, draft);
    if (shot < shotsPerShooter) focusAfter(cellKey(shot));
  }

  function onShotChange(shot: number, raw: string) {
    const key = cellKey(shot);
    if (whole) {
      const entry = strictWholeShotEntry(raw, cellsRef.current[key]?.draft ?? '');
      patchCell(key, { draft: entry.draft, inputRejected: entry.rejected, status: entry.rejected ? 'error' : entry.draft ? 'typing' : 'empty', error: entry.rejected ? WHOLE_SHOT_MESSAGE : null });
      if (entry.advance) {
        finishWholeShot(shot, entry.draft);
        return;
      }
      return;
    }
    const draft = normalizeShotDraft(raw);
    patchCell(key, { draft, status: draft ? 'typing' : 'empty', error: null });
    if (isCompleteShot(draft)) {
      void commit(shot, draft);
      focusAfter(key);
    }
  }

  function onShotBlur(shot: number) {
    const cell = cellsRef.current[cellKey(shot)];
    if (!cell) return;
    if (cell.status === 'typing' || (cell.status === 'empty' && (cell.scoreId || pending.current.has(`${formatKey}:${cellKey(shot)}`)))) void commit(shot);
  }

  function onShotKeyDown(event: React.KeyboardEvent<HTMLInputElement>, shot: number) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    void commit(shot);
    focusAfter(cellKey(shot));
  }

  const standing = detail?.leaderboard.rows.find((row) => row.shooterId === shooterId) ?? null;
  const genderCounts = useMemo(() => {
    const counts: Record<GenderFilter, number> = { all: 0, male: 0, female: 0 };
    for (const person of detail?.shooters ?? []) {
      counts.all += 1;
      if (person.gender === 'male' || person.gender === 'female') counts[person.gender] += 1;
    }
    return counts;
  }, [detail]);
  const roster = detail
    ? detail.shooters.filter(
        (person) => genderFilter === 'all' || person.gender === genderFilter,
      )
    : [];
  const teams = detail?.leaderboard.round1?.teams ?? [];
  const teamMode = genderFilter === 'all' && teams.some((team) => team.teamId && team.members?.some((member) => member.shooterId === shooterId));
  const teamNext = detail && shooterId && teamMode
    ? nextTeamShooter(detail.shooters, teams, shooterId, completedShooters(detail.scores, shotsPerShooter)) : null;
  const next = teamMode ? teamNext?.shooter ?? null : detail && shooterId ? nextShooter(roster, shooterId, query) : null;

  function setGenderFilter(value: GenderFilter) {
    const nextParams = new URLSearchParams(searchParams);
    if (value === 'all') nextParams.delete('gender');
    else nextParams.set('gender', value);
    setSearchParams(nextParams, { replace: true });
  }
  const typed = runningTotal(order.map((key) => cells[key]?.draft ?? ''), countedShots);
  const runningTenths = typed.tenths;
  const savedCount = order.filter((key) => cells[key]?.status === 'saved').length;
  const unsaved = order.filter((key) => ['typing', 'saving', 'error'].includes(cells[key]?.status ?? '')).length;
  const errors = order.map((key) => cells[key]).filter((cell) => cell?.status === 'error');
  const complete = order.length > 0 && savedCount === order.length;
  const navigationBlocked = saveCount > 0 || unsaved > 0;

  function goBack(event?: React.MouseEvent) {
    if (navigationBlocked) {
      event?.preventDefault();
      return;
    }
    if (cameFromList) {
      event?.preventDefault();
      navigate(-1);
    }
  }

  function goNext() {
    if (!next || navigationBlocked) return;
    focusFirst.current = true;
    navigate(scoringShooterPath(slug, next, query, groupQuery, genderQuery), { replace: true, state: cameFromList ? { fromList: true } : null });
  }

  useEffect(() => {
    if (autoAdvance.current !== formatKey || !complete || saveCount > 0) return;
    autoAdvance.current = null;
    if (document.activeElement === inputs.current.get(cellKey(shotsPerShooter))) goNext();
  }, [complete, saveCount, formatKey, next?.id]);

  /** Every saved row for this shooter (what "Clear scores" removes). */
  const storedCount = detail && shooterId ? detail.scores.filter((score) => score.shooterId === shooterId).length : 0;

  useEffect(() => {
    setClearOpen(false);
    setRemoveOpen(false);
    setClearedMessage(null);
  }, [shooterId]);

  async function saveGender(gender: ShooterGender | null) {
    if (!detail || !shooterId || genderSaving) return;
    setGenderSaving(true);
    setGenderError(null);
    try {
      const body = await apiFetch<{ shooter: ShooterRow | null; leaderboard: TournamentDetail['leaderboard'] }>(
        `/api/admin/shooting/tournaments/${detail.tournament.id}/shooters/${shooterId}`,
        { method: 'PATCH', body: JSON.stringify({ gender }) },
      );
      setDetail((current) => ({
        ...current,
        shooters: current.shooters.map((row) => (row.id === shooterId ? { ...row, gender: body.shooter?.gender ?? gender } : row)),
        leaderboard: body.leaderboard,
      }));
    } catch (err) {
      setGenderError(err instanceof ApiError ? err.message : 'Could not save. Try again.');
    } finally {
      setGenderSaving(false);
    }
  }

  function onRemoved(result: { removedShots: number; leaderboard: TournamentDetail['leaderboard'] }) {
    if (!shooterId || !shooter) return;
    setRemoveOpen(false);
    setDetail((current) => ({
      ...current,
      shooters: current.shooters.filter((row) => row.id !== shooterId),
      scores: current.scores.filter((score) => score.shooterId !== shooterId),
      leaderboard: result.leaderboard,
    }));
    pushScoringToast(removedToast(shooter.name));
    navigate(listPath, { replace: true });
  }

  function onCleared(result: { cleared: number; leaderboard: TournamentDetail['leaderboard'] }) {
    if (!shooterId) return;
    for (const id of persisted.current.keys()) if (id.startsWith(`${formatKey}:`)) persisted.current.delete(id);
    autoAdvance.current = null;
    const fresh = cellsFromScores([], shooterId, shotsPerShooter);
    cellsRef.current = fresh;
    setCells(fresh);
    setDetail((current) => ({
      ...current,
      scores: current.scores.filter((score) => score.shooterId !== shooterId),
      leaderboard: result.leaderboard,
    }));
    setClearOpen(false);
    setClearedMessage(`Cleared ${result.cleared} shot${result.cleared === 1 ? '' : 's'}. The public board is updated.`);
    pushScoringToast(scoresClearedToast());
  }

  const navButtons = (
    <div className="grid grid-cols-2 gap-2">
      <Link
        to={listPath}
        onClick={goBack}
        aria-disabled={navigationBlocked}
        className="inline-flex min-h-12 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-surface-2)] px-3 text-sm font-medium text-[var(--color-text-primary)]"
      >
        Back to list
      </Link>
      {next ? (
        <button
          type="button"
          onClick={goNext}
          disabled={navigationBlocked}
          className={`min-h-12 truncate rounded-[var(--radius-md)] px-3 text-sm font-medium ${
            complete ? 'bg-[var(--color-accent)] text-white' : 'bg-[var(--color-surface-2)] text-[var(--color-text-primary)]'
          }`}
        >
          {teamNext?.sameTeam ? 'Next teammate: ' : teamNext?.team ? `Next team: ${teamNext.team.name} · ` : 'Next: '}{next.name}
        </button>
      ) : (
        <span className="inline-flex min-h-12 items-center justify-center text-sm text-[var(--color-text-tertiary)]">
          {teamMode && !complete ? 'Finish this shooter to continue' : teamMode ? 'All team shooters complete' : 'Last shooter in the list'}
        </span>
      )}
    </div>
  );

  if (detail?.tournament.status === 'final') return <FinalTournamentView detail={detail} />;

  return (
    <Layout>
      <div className="mx-auto max-w-xl space-y-4">
        <nav>
          <Link
            to={listPath}
            onClick={goBack}
            className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
          >
            <span aria-hidden="true">←</span> All shooters
          </Link>
        </nav>

        {error ? (
          <p className="rounded-[var(--radius-md)] bg-[var(--color-error-subtle)] px-4 py-3 text-sm text-[var(--color-error-text)]">
            {error}
          </p>
        ) : null}

        {notFound ? (
          <p className="text-sm text-[var(--color-text-secondary)]">There is no tournament called “{slug}”.</p>
        ) : !detail ? (
          error ? null : <p className="text-sm text-[var(--color-text-secondary)]">Loading…</p>
        ) : !shooter ? (
          <p className="rounded-[var(--radius-lg)] border border-dashed border-[var(--color-border-strong)] px-4 py-8 text-sm text-[var(--color-text-secondary)]">
            No shooter with the number “{shooterKey}” in {detail.tournament.title}. Go back and pick them from the list.
          </p>
        ) : (
          <>
            <header className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-[var(--color-text-tertiary)]">{detail.tournament.title}</p>
                <h1 className="truncate text-2xl font-semibold text-[var(--color-text-primary)]">{shooter.name}</h1>
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[var(--color-text-tertiary)]">
                  <span>{shooter.bib ?? 'No number'}</span>
                  {shooter.teamName ? <span>· {shooter.teamName}</span> : null}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-3xl font-semibold tabular-nums text-[var(--color-text-primary)]" aria-live="polite">
                  {showShotPoints(runningTenths, whole)}
                </p>
                <p className="text-xs text-[var(--color-text-tertiary)]">
                  {standing && standing.rank > 0 ? `Rank ${standing.rank} · ` : ''}total max {detail.tournament.maxDisplay ?? formatMaxDisplay(format)}{whole ? ' · 10 per shot' : ''}
                </p>
              </div>
            </header>

            {isAdmin ? (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {isAdmin ? <GenderRosterFilter value={genderFilter} counts={genderCounts} onChange={setGenderFilter} /> : null}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[var(--color-text-secondary)]" id="shooter-group-label">
                Men / Women
              </span>
              <div className="flex gap-1" role="group" aria-labelledby="shooter-group-label">
                {([...SHOOTER_GENDERS, null] as (ShooterGender | null)[]).map((value) => {
                  const selected = (shooter.gender ?? null) === value;
                  return (
                    <button
                      key={value ?? 'none'}
                      type="button"
                      aria-pressed={selected}
                      disabled={genderSaving}
                      onClick={() => void saveGender(value)}
                      className={`min-h-11 rounded-full px-4 text-sm font-medium disabled:opacity-60 ${
                        selected
                          ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)]'
                          : 'border border-[var(--color-border)] text-[var(--color-text-secondary)]'
                      }`}
                    >
                      {value ? SHOOTER_GENDER_LABELS[value] : 'Not set'}
                    </button>
                  );
                })}
              </div>
              {genderError ? (
                <p className="w-full text-sm text-[var(--color-error-text)]" role="alert">
                  {genderError}
                </p>
              ) : null}
            </div>

            {added ? (
              <p className="text-sm text-[var(--color-success-text)]" role="status">
                {shooter.name} added.
              </p>
            ) : null}

            <section className="space-y-4 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)] p-4">
              <p className="text-sm text-[var(--color-text-secondary)]">
                {whole
                  ? `Whole numbers 0–10. ${allCount ? `All ${shotsPerShooter} count.` : `The best ${countedShots} of the ${shotsPerShooter} shots count.`} For 1, press Tab or Enter to move on.`
                  : allCount
                    ? `Type a decimal, like 9.7 or 10.4. All ${shotsPerShooter} shots count.`
                    : `Type a decimal, like 9.7 or 10.4. The best ${countedShots} of the ${shotsPerShooter} shots count.`}
              </p>

              <div className="space-y-2">
                <p className="text-right text-sm text-[var(--color-text-secondary)]">
                  Total{' '}
                  <span className="text-lg font-semibold tabular-nums text-[var(--color-text-primary)]">
                    {showShotPoints(runningTenths, whole)}
                  </span>{' '}
                  <span className="tabular-nums">
                    {allCount ? `(${typed.count}/${shotsPerShooter})` : `(best ${countedShots} of ${typed.count}/${shotsPerShooter})`}
                  </span>
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {SHOT_NUMBERS.map((shot) => {
                    const key = cellKey(shot);
                    const cell = cells[key];
                    const status = cell?.status ?? 'empty';
                    const dropped = !allCount && typed.dropped.has(shot) && status !== 'error' && status !== 'typing';
                    const border = dropped
                      ? 'border-[var(--color-border-strong)]'
                      : status === 'error'
                        ? 'border-[var(--color-error)] bg-[var(--color-error-subtle)]'
                        : status === 'saved'
                          ? 'border-[var(--color-success)]'
                          : status === 'typing' || status === 'saving'
                            ? 'border-[var(--color-accent)]'
                            : 'border-[var(--color-border-strong)]';
                    return (
                      <label key={key} className="block">
                        <span className="flex items-center justify-between px-0.5 text-xs text-[var(--color-text-tertiary)]">
                          <span>{shot}</span>
                          {dropped && status !== 'saving' ? (
                            <span className="text-[11px] leading-none">out</span>
                          ) : (
                            <span aria-hidden="true" className={status === 'saved' ? 'text-[var(--color-success-text)]' : ''}>
                              {status === 'saved' ? '✓' : status === 'saving' ? '…' : status === 'error' ? '!' : ''}
                            </span>
                          )}
                        </span>
                        <input
                          ref={(element) => {
                            if (element) inputs.current.set(key, element);
                            else inputs.current.delete(key);
                          }}
                          aria-label={dropped ? `Shot ${shot}, not counted` : `Shot ${shot}`}
                          aria-invalid={status === 'error'}
                          inputMode={whole ? 'numeric' : 'decimal'}
                          enterKeyHint="next"
                          autoComplete="off"
                          maxLength={whole ? 16 : 4}
                          placeholder="–"
                          value={cell?.draft ?? ''}
                          onChange={(event) => onShotChange(shot, event.target.value)}
                          onBlur={() => onShotBlur(shot)}
                          onKeyDown={(event) => onShotKeyDown(event, shot)}
                          onFocus={(event) => { autoAdvance.current = null; event.target.select(); }}
                          className={`h-14 w-full min-w-0 rounded-[var(--radius-md)] border-2 bg-[var(--color-surface-1)] px-0 text-center text-lg font-semibold tabular-nums tracking-tight ${border} ${status === 'saving' ? 'opacity-70' : ''} ${
                            dropped
                              ? 'border-dashed bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)] line-through'
                              : 'text-[var(--color-text-primary)]'
                          }`}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              {typed.dropped.size > 0 ? (
                <p className="text-xs text-[var(--color-text-tertiary)]">
                  Struck-through shots are the lowest {typed.dropped.size} and are not counted.
                </p>
              ) : null}
              <p className="text-sm text-[var(--color-text-secondary)]" role="status">
                {savedCount} of {order.length} shots saved
                {(unsaved > 0 || saveCount > 0) && errors.length === 0 ? ' · saving…' : ''}
              </p>
              {errors.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm text-[var(--color-error-text)]">{errors[0]?.error ?? SHOT_RANGE_MESSAGE}</p>
                  <button type="button" disabled={saveCount > 0} className="min-h-11 text-sm font-medium text-[var(--color-accent)]" onClick={() => {
                    for (const shot of SHOT_NUMBERS) if (cellsRef.current[cellKey(shot)]?.status === 'error') void commit(shot);
                  }}>Retry failed shots</button>
                </div>
              ) : null}
            </section>

            {complete ? (
              <div
                className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-success)] bg-[var(--color-surface-1)] p-4"
                role="status"
              >
                <p className="font-medium text-[var(--color-success-text)]">
                  All {order.length} shots saved. Total {showShotPoints(runningTenths, whole)}
                  {allCount ? '.' : ` (best ${countedShots}).`}
                </p>
                {navButtons}
              </div>
            ) : (
              navButtons
            )}

            {clearedMessage ? (
              <p className="text-sm text-[var(--color-success-text)]" role="status">
                {clearedMessage}
              </p>
            ) : null}
            {storedCount > 0 || isAdmin ? (
              <div className="flex flex-wrap justify-end gap-3 border-t border-[var(--color-border)] pt-6">
                {isAdmin ? (
                  <button
                    type="button"
                    onClick={() => setRemoveOpen(true)}
                    disabled={saveCount > 0}
                    className="min-h-11 rounded-[var(--radius-md)] px-4 text-sm font-medium text-[var(--color-error-text)] underline-offset-2 hover:underline"
                  >
                    Remove from tournament
                  </button>
                ) : null}
                {storedCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => setClearOpen(true)}
                    disabled={saveCount > 0}
                    className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-error)] px-4 text-sm font-medium text-[var(--color-error-text)]"
                  >
                    Clear scores
                  </button>
                ) : null}
              </div>
            ) : null}
            {removeOpen && shooterId && isAdmin ? (
              <RemoveShooterDialog
                tournamentId={detail.tournament.id}
                tournamentTitle={detail.tournament.title}
                shooterId={shooterId}
                shooterName={shooter.name}
                shooterBib={shooter.bib}
                shotCount={storedCount}
                onClose={() => setRemoveOpen(false)}
                onRemoved={onRemoved}
              />
            ) : null}
            {clearOpen && shooterId ? (
              <ClearScoresDialog
                tournamentId={detail.tournament.id}
                shooterId={shooterId}
                shooterName={shooter.name}
                shotCount={storedCount}
                onClose={() => setClearOpen(false)}
                onCleared={onCleared}
              />
            ) : null}
          </>
        )}
      </div>
    </Layout>
  );
}
